import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import pdfWorkerSrc from 'pdfjs-dist/build/pdf.worker.mjs?url'
import { formatDate, formatTime, getDateOnly } from '../../helpers/formatDate'
import { formatMoney, parseDocumentType, isDocumentCredit } from '../../helpers/utils'
import { buildPaymentBreakdownRows, buildReceiptRows, safeArray, toNumber } from './detailsHelpers'
import { parseRefundType } from './constants'

// Constants for PDF layout and styling

const TABLE_HEADER_FILL = [217, 217, 217]
const TABLE_BORDER_COLOR = [150, 150, 150]
const SOFT_LINE_COLOR = [215, 215, 215]
const PAGE_MARGIN_X = 20
const FOOTER_TOP_OFFSET = 36
const FOOTER_TEXT_OFFSET = 24
const PDFJS_WORKER_SRC = pdfWorkerSrc
const CONTENT_TOP_Y = 70
const SECTION_MIN_HEIGHT = 72
const FIRST_PAGE_SUMMARY_WIDTH = 442
const FIRST_PAGE_CASH_LEFT = 476
const FIRST_PAGE_CASH_WIDTH = 296
const FULL_TABLE_WIDTH = 752
const FIRST_PAGE_TABLE_START_Y = 122
const RECEIVED_DOCUMENT_ROUTE_HEADER = 'Cod. Fact. Electr.'
const RECEIVED_DOCUMENT_HEADERS = [
  'Fecha',
  'Hora',
  'Código',
  'Cliente',
  'N° Documento',
  'Tipo Doc.',
  'Referencia',
  'Forma Pago',
  'Saldo',
  'Pagado'
]
const RECEIVED_DOCUMENT_COLUMN_STYLES = {
  0: { cellWidth: 56 },
  1: { cellWidth: 52 },
  2: { cellWidth: 43 },
  3: { cellWidth: 207 },
  4: { cellWidth: 74 },
  5: { cellWidth: 54 },
  6: { cellWidth: 90 },
  7: { cellWidth: 65 },
  8: { cellWidth: 55, halign: 'right' },
  9: { cellWidth: 56, halign: 'right' }
}
const RECEIVED_DOCUMENT_COLUMN_STYLES_WITH_ROUTE = {
  0: { cellWidth: 54 },
  1: { cellWidth: 48 },
  2: { cellWidth: 42 },
  3: { cellWidth: 150 },
  4: { cellWidth: 70 },
  5: { cellWidth: 50 },
  6: { cellWidth: 86 },
  7: { cellWidth: 82 },
  8: { cellWidth: 60 },
  9: { cellWidth: 54, halign: 'right' },
  10: { cellWidth: 56, halign: 'right' }
}

// Helper function to create common table options for jsPDF autoTable
const createCommonTableOptions = (overrides = {}) => ({
  theme: 'grid',
  styles: {
    font: 'helvetica',
    fontSize: 8,
    cellPadding: 3,
    halign: 'left',
    valign: 'middle',
    overflow: 'linebreak',
    lineColor: TABLE_BORDER_COLOR,
    lineWidth: 0.35,
    minCellHeight: 18.5,
    ...(overrides.styles || {})
  },
  headStyles: {
    fillColor: TABLE_HEADER_FILL,
    textColor: [0, 0, 0],
    fontStyle: 'bold',
    font: 'helvetica',
    fontSize: 9,
    halign: 'left',
    valign: 'middle',
    lineColor: TABLE_BORDER_COLOR,
    lineWidth: 0.35,
    ...(overrides.headStyles || {})
  },
  margin: {
    left: PAGE_MARGIN_X,
    right: PAGE_MARGIN_X,
    bottom: 54,
    ...(overrides.margin || {})
  }
})

const getPageWidth = (doc) => doc.internal.pageSize.getWidth()
const getPageHeight = (doc) => doc.internal.pageSize.getHeight()
const getPageRight = (doc) => getPageWidth(doc) - PAGE_MARGIN_X

const formatPdfDate = (date) => (date ? getDateOnly(date) || 'N/A' : 'N/A')

const parseValidDate = (date) => {
  if (!date) return null

  const parsedDate = new Date(date)
  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate
}

const formatChargeDateTime = (date) => {
  const parsedDate = parseValidDate(date)
  if (!parsedDate) return 'N/A'

  const time = parsedDate.toLocaleTimeString('es', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  })

  return `${formatDate(parsedDate)}, ${time}`
}

const formatPdfDateTime = (date, options = {}) => {
  const parsedDate = parseValidDate(date)
  if (!parsedDate) return 'N/A'

  const formatter = new Intl.DateTimeFormat('es', {
    day: options.twoDigitDate ? '2-digit' : 'numeric',
    month: options.twoDigitDate ? '2-digit' : 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true
  })

  return formatter
    .format(parsedDate)
    .replace(/\s*a\.\s*m\./i, ' a.m.')
    .replace(/\s*p\.\s*m\./i, ' p.m.')
}

const normalizePdfText = (value, fallback = 'N/A') => {
  if (value === null || value === undefined || value === '') return fallback
  return String(value)
}

export const buildReceivedDocumentsPdfTable = (details = [], canWrite = false) => {
  const headers = canWrite
    ? [
        ...RECEIVED_DOCUMENT_HEADERS.slice(0, 6),
        RECEIVED_DOCUMENT_ROUTE_HEADER,
        ...RECEIVED_DOCUMENT_HEADERS.slice(6)
      ]
    : RECEIVED_DOCUMENT_HEADERS
  const rows = []
  const rawData = []

  safeArray(details).forEach((detail) => {
    const isCredit = isDocumentCredit(detail)
    const balance = Number(detail.child_document_balance || 0)
    const paid = isCredit ? 0 : Number(detail.child_document_amount || 0)
    const row = [
      formatDate(detail.date_of_charge) || 'N/A',
      formatTime(detail.date_of_charge) || 'N/A',
      detail.client_uid || 'N/A',
      detail.client_name || 'N/A',
      detail.child_document_reference || 'N/A',
      parseDocumentType(detail.child_document_type) || 'N/A'
    ]

    if (canWrite) {
      row.push(detail.child_document_route || 'N/A')
    }

    row.push(
      detail.charge_uid ? detail.charge_uid : 'N/A',
      isCredit ? 'Crédito' : 'Contado',
      formatMoney(balance),
      formatMoney(paid)
    )

    if (detail.charge_status === 'received' || detail.client_payment_type === 'credito') {
      rows.push(row)

      rawData.push({
        tipo: parseDocumentType(detail.child_document_type) || 'N/A',
        total: paid
      })
    }
  })

  if (rows.length === 0) {
    rows.push(Array(headers.length).fill('N/A'))
  }

  return {
    headers,
    rows,
    rawData,
    columnStyles: canWrite
      ? RECEIVED_DOCUMENT_COLUMN_STYLES_WITH_ROUTE
      : RECEIVED_DOCUMENT_COLUMN_STYLES
  }
}

const firstPresentValue = (...values) => {
  const value = values.find(
    (current) => current !== null && current !== undefined && String(current).trim() !== ''
  )

  return value === undefined ? 'N/A' : String(value)
}

const sanitizeFilenamePart = (value) =>
  firstPresentValue(value)
    .replace(/[\\/:*?"<>|]+/g, '-')
    .replace(/\s+/g, ' ')
    .trim()

const isHttpUrl = (url = '') => /^https?:\/\//i.test(String(url).trim())
const isUnavailableLocalUrl = (url = '') => /^(content|file):\/\//i.test(String(url).trim())
const getUrlPath = (url = '') => String(url).split(/[?#]/, 1)[0].toLowerCase()
const isPdfUrl = (url = '', filename = '') =>
  getUrlPath(url).endsWith('.pdf') ||
  String(filename || '')
    .toLowerCase()
    .endsWith('.pdf')
const isImageUrl = (url = '', filename = '') =>
  /\.(jpe?g|png|gif|webp|bmp)$/i.test(getUrlPath(url)) ||
  /\.(jpe?g|png|gif|webp|bmp)$/i.test(String(filename || ''))

const inferImageFormat = (mime = '', dataUrl = '') => {
  const source = `${mime} ${dataUrl}`.toLowerCase()
  if (source.includes('png')) return 'PNG'
  if (source.includes('webp')) return 'WEBP'
  return 'JPEG'
}

const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result)
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })

const getImageSize = (dataUrl) =>
  new Promise((resolve, reject) => {
    const image = new Image()
    image.onload = () =>
      resolve({
        width: image.naturalWidth || image.width,
        height: image.naturalHeight || image.height
      })
    image.onerror = reject
    image.src = dataUrl
  })

export const loadImageDataUrl = async (url) => {
  const response = await fetch(url)
  if (!response.ok) {
    throw new Error(`No se pudo cargar la imagen del comprobante: ${response.status}`)
  }

  const blob = await response.blob()
  const dataUrl = await blobToDataUrl(blob)
  const size = await getImageSize(dataUrl)

  return {
    dataUrl,
    format: inferImageFormat(blob.type, dataUrl),
    width: size.width,
    height: size.height
  }
}

const getPdfJs = async () => {
  const pdfjsLib = await import('pdfjs-dist')

  if (pdfjsLib?.GlobalWorkerOptions && !pdfjsLib.GlobalWorkerOptions.workerSrc) {
    pdfjsLib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER_SRC
  }

  return pdfjsLib
}

export const renderPdfToImageDataUrls = async (url) => {
  const response = await fetch(url)
  if (!response.ok) {
    throw new Error(`No se pudo cargar el adjunto PDF: ${response.status}`)
  }

  const buffer = await response.arrayBuffer()
  const pdfjsLib = await getPdfJs()
  const pdf = await pdfjsLib.getDocument({ data: new Uint8Array(buffer) }).promise
  try {
    const pages = []

    for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
      const page = await pdf.getPage(pageNumber)
      const viewport = page.getViewport({ scale: 1.8 })
      const canvas = document.createElement('canvas')
      const context = canvas.getContext('2d')

      canvas.width = viewport.width
      canvas.height = viewport.height

      await page.render({ canvasContext: context, viewport }).promise

      pages.push({
        dataUrl: canvas.toDataURL('image/png'),
        format: 'PNG',
        width: canvas.width,
        height: canvas.height,
        pageNumber
      })
    }

    return pages
  } finally {
    pdf.destroy?.()
  }
}

const fitInside = ({ width, height }, maxWidth, maxHeight) => {
  const scale = Math.min(maxWidth / width, maxHeight / height)
  return {
    width: width * scale,
    height: height * scale
  }
}

const drawInlineLabel = (doc, label, value, x, y, gap = 4) => {
  doc.setFont('helvetica', 'bold')
  const labelWidth = doc.getTextWidth(label)
  doc.text(label, x, y)
  doc.setFont('helvetica', 'normal')
  doc.text(value, x + labelWidth + gap, y)
}

const drawRightInlineLabel = (doc, label, value, rightX, y) => {
  doc.setFont('helvetica', 'bold')
  const labelWidth = doc.getTextWidth(label)
  doc.setFont('helvetica', 'normal')
  const valueWidth = doc.getTextWidth(value)
  const gap = 4
  const startX = rightX - labelWidth - valueWidth - gap

  drawInlineLabel(doc, label, value, startX, y, gap)
}

const drawSectionTitle = (doc, title, x, y, fontSize = 8.5) => {
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(fontSize)
  doc.setTextColor(0, 0, 0)
  doc.text(title, x, y)
}

const getContentBottomY = (doc) => getPageHeight(doc) - FOOTER_TOP_OFFSET - 14

const ensureSectionStart = (doc, startY, minHeight = SECTION_MIN_HEIGHT) => {
  if (startY + minHeight <= getContentBottomY(doc)) return startY

  doc.addPage()
  return CONTENT_TOP_Y
}

const drawChargePageHeader = (doc, settlement) => {
  const right = getPageRight(doc)
  const userText = `${settlement?.user_uid || 'N/A'} - ${settlement?.user_full_name || 'N/A'}`

  doc.setFontSize(8)
  doc.setTextColor(0, 0, 0)
  drawInlineLabel(doc, 'Usuario:', userText, PAGE_MARGIN_X, 42)
  drawRightInlineLabel(doc, 'N° de Liquidación:', settlement?.uid || 'N/A', right, 42)
  doc.setDrawColor(...SOFT_LINE_COLOR)
  doc.setLineWidth(0.5)
  doc.line(PAGE_MARGIN_X, 54, right, 54)
}

const drawFooterOnPage = (doc, pageNumber, totalPages, printDate) => {
  const pageHeight = getPageHeight(doc)
  const right = getPageRight(doc)
  const lineY = pageHeight - FOOTER_TOP_OFFSET
  const textY = pageHeight - FOOTER_TEXT_OFFSET

  doc.setDrawColor(...SOFT_LINE_COLOR)
  doc.setLineWidth(0.5)
  doc.line(PAGE_MARGIN_X, lineY, right, lineY)
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(8)
  doc.setTextColor(120, 120, 120)
  doc.text(`Fecha de creación: ${printDate}`, PAGE_MARGIN_X, textY)
  doc.text(`Página ${pageNumber} de ${totalPages}`, right, textY, { align: 'right' })
  doc.setTextColor(0, 0, 0)
}

const addPageFooters = (doc, printDate = getDateOnly(new Date())) => {
  const totalPages = doc.internal.getNumberOfPages()

  for (let pageNumber = 1; pageNumber <= totalPages; pageNumber += 1) {
    doc.setPage(pageNumber)
    drawFooterOnPage(doc, pageNumber, totalPages, printDate)
  }
}

const getCollectionFallbackChargeUid = (payment, details = []) => {
  if (payment?.charge_uid) return payment.charge_uid
  if (
    payment?.collection_uid &&
    details.some((detail) => detail.charge_uid === payment.collection_uid)
  ) {
    return payment.collection_uid
  }

  const candidates = details.filter((detail) => detail.client_uid === payment?.client_uid)
  if (candidates.length === 1) return candidates[0].charge_uid

  const amount = toNumber(payment?.amount, 0)
  const exactMatches = candidates.filter(
    (detail) =>
      toNumber(detail?.total_amount, 0) === amount ||
      toNumber(detail?.child_document_amount, 0) === amount
  )

  return exactMatches.length === 1 ? exactMatches[0].charge_uid : null
}

const buildFallbackPaymentRows = (settlement) => {
  const details = safeArray(settlement?.details)

  return safeArray(settlement?.collection_payments)
    .filter((payment) => {
      const type = String(payment?.payment_mode || '')
        .trim()
        .toLowerCase()
      return !(type === 'efectivo' && toNumber(payment?.amount, 0) === 0)
    })
    .map((payment, index) => ({
      id: payment.id || `collection-payment-${index}`,
      chargeUid: getCollectionFallbackChargeUid(payment, details),
      date: payment.date || payment.created_at || null,
      paymentType: payment.payment_mode || payment.payment_type || 'N/A',
      amount: toNumber(payment.amount, 0),
      bank: payment.bank_name || payment.bank_short_name || null,
      reference: payment.reference || null,
      images: [],
      files: []
    }))
    .filter((payment) => payment.chargeUid)
}

const getSettlementPaymentRows = (settlement) => {
  if (safeArray(settlement?.payment_breakdown).length > 0) {
    return buildPaymentBreakdownRows(settlement)
  }

  const detailPaymentRows = buildPaymentBreakdownRows(settlement)
  return detailPaymentRows.length > 0 ? detailPaymentRows : buildFallbackPaymentRows(settlement)
}

const ensureChargeGroup = (groups, order, chargeUid) => {
  if (!chargeUid) return null

  if (!groups.has(chargeUid)) {
    groups.set(chargeUid, {
      chargeUid,
      documents: [],
      payments: [],
      hasEvidence: false
    })
    order.push(chargeUid)
  }

  return groups.get(chargeUid)
}

export const buildSettlementChargeGroups = (settlement) => {
  const groups = new Map()
  const order = []

  safeArray(settlement?.details).forEach((detail) => {
    const chargeUid = detail?.charge_uid
    const group = ensureChargeGroup(groups, order, chargeUid)
    if (group) group.documents.push(detail)
  })

  getSettlementPaymentRows(settlement).forEach((payment) => {
    const group = ensureChargeGroup(groups, order, payment.chargeUid)
    if (!group) return

    const paymentKey = [
      payment.id || '',
      payment.paymentType || '',
      payment.reference || '',
      payment.amount
    ].join('|')

    if (!group.payments.some((current) => current._pdfKey === paymentKey)) {
      group.payments.push({ ...payment, _pdfKey: paymentKey })
    }

    if (safeArray(payment.images).length > 0 || safeArray(payment.files).length > 0) {
      group.hasEvidence = true
    }
  })

  // Always include charge pages (N° cobro / cliente / docs / pagos).
  // Evidence image/file pages are added later only when hasEvidence is true.
  return order.map((chargeUid) => groups.get(chargeUid)).filter(Boolean)
}

const buildChargeDocumentRows = (documents = []) =>
  documents.map((detail) => [
    normalizePdfText(detail?.child_document_reference || detail?.child_uid),
    formatPdfDate(detail?.child_document_date),
    formatPdfDate(detail?.child_document_due_date || detail?.document_due_date),
    parseDocumentType(detail?.child_document_type) || 'N/A',
    formatMoney(detail?.child_document_total ?? detail?.child_document_balance ?? 0),
    formatMoney(detail?.child_early_payment_discount_amount || 0),
    formatMoney(detail?.child_document_amount || 0)
  ])

const buildChargePaymentRows = (payments = []) =>
  payments.map((payment) => [
    normalizePdfText(payment?.paymentType),
    formatPdfDate(payment?.date || payment?.created_at),
    normalizePdfText(payment?.bank),
    normalizePdfText(payment?.reference),
    formatMoney(payment?.amount || 0)
  ])

const getDocumentsTotal = (documents = []) =>
  documents.reduce((sum, detail) => sum + toNumber(detail?.child_document_amount, 0), 0)

const getPaymentsTotal = (payments = []) =>
  payments.reduce((sum, payment) => sum + toNumber(payment?.amount, 0), 0)

const drawRightTotal = (doc, label, value, y) => {
  const right = getPageRight(doc)

  doc.setFont('helvetica', 'bold')
  doc.setFontSize(8)
  doc.text(label, right - 64, y, { align: 'right' })
  doc.setFont('helvetica', 'normal')
  doc.text(value, right, y, { align: 'right' })
}

const addChargeDetailPage = (doc, settlement, group) => {
  doc.addPage()
  drawChargePageHeader(doc, settlement)

  const firstDocument = group.documents[0] || {}
  const clientText =
    firstDocument.client_uid || firstDocument.client_name
      ? `${firstDocument.client_uid || 'N/A'} - ${firstDocument.client_name || 'N/A'}`
      : 'N/A'

  doc.setFontSize(8)
  drawInlineLabel(doc, 'N° de Cobro:', group.chargeUid || 'N/A', PAGE_MARGIN_X, 76)
  drawInlineLabel(doc, 'Cliente:', clientText, PAGE_MARGIN_X, 88)
  drawInlineLabel(
    doc,
    'Fecha de creación:',
    formatChargeDateTime(firstDocument.date_of_charge),
    PAGE_MARGIN_X,
    100
  )

  drawSectionTitle(doc, 'DOCUMENTOS:', PAGE_MARGIN_X, 130, 9)

  autoTable(doc, {
    head: [['N° Documento', 'Fecha', 'Vencimiento', 'Tipo', 'Saldo', 'Descuento', 'Pagado']],
    body: buildChargeDocumentRows(group.documents),
    startY: 140,
    ...createCommonTableOptions({
      styles: { fontSize: 8, cellPadding: 3 },
      headStyles: { fontSize: 8, halign: 'left' }
    }),
    columnStyles: {
      4: { halign: 'right' },
      5: { halign: 'right' },
      6: { halign: 'right' }
    }
  })

  drawRightTotal(
    doc,
    'TOTAL DOCUMENTOS APLICADOS:',
    formatMoney(getDocumentsTotal(group.documents)),
    doc.lastAutoTable.finalY + 14
  )

  const paymentsStartY = doc.lastAutoTable.finalY + 44

  doc.setFont('helvetica', 'bold')
  doc.setFontSize(9)
  doc.text('MÉTODOS DE PAGO:', PAGE_MARGIN_X, paymentsStartY - 10)

  autoTable(doc, {
    head: [['Tipo de pago', 'Fecha', 'Banco', 'N° de referencia', 'Monto']],
    body: buildChargePaymentRows(group.payments),
    startY: paymentsStartY,
    ...createCommonTableOptions({
      styles: { fontSize: 8, cellPadding: 3 },
      headStyles: { fontSize: 8, halign: 'left' }
    }),
    columnStyles: {
      4: { halign: 'right' }
    }
  })

  drawRightTotal(
    doc,
    'TOTAL PAGADO POR EL CLIENTE:',
    formatMoney(getPaymentsTotal(group.payments)),
    doc.lastAutoTable.finalY + 14
  )
}

const addEvidenceTitle = (doc, title, subtitle) => {
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(11)
  doc.text(title, PAGE_MARGIN_X, 48)
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(8)
  doc.setTextColor(60, 60, 60)
  doc.text(subtitle, PAGE_MARGIN_X, 64)
  doc.setTextColor(0, 0, 0)
}

const getEvidenceSubtitle = (payment) => {
  const parts = [
    payment?.paymentType,
    payment?.bank,
    payment?.reference ? `Ref. ${payment.reference}` : null
  ].filter(Boolean)

  return parts.length > 0 ? parts.join(' — ') : 'Comprobante del pago'
}

const drawEvidencePlaceholder = (doc) => {
  const pageWidth = getPageWidth(doc)
  const rectWidth = 420
  const rectHeight = 420
  const rectX = (pageWidth - rectWidth) / 2
  const rectY = 86

  doc.setFillColor(242, 242, 242)
  doc.rect(rectX, rectY, rectWidth, rectHeight, 'F')
  doc.setTextColor(130, 130, 130)
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(8)
  doc.text('[ comprobante no disponible ]', pageWidth / 2, rectY + rectHeight / 2, {
    align: 'center'
  })
  doc.setTextColor(0, 0, 0)
}

const drawEvidenceImage = (doc, imageData) => {
  const maxWidth = getPageWidth(doc) - 2 * PAGE_MARGIN_X - 130
  const maxHeight = getPageHeight(doc) - 150
  const size = fitInside(imageData, maxWidth, maxHeight)
  const x = (getPageWidth(doc) - size.width) / 2
  const y = 86 + (maxHeight - size.height) / 2

  doc.addImage(imageData.dataUrl, imageData.format || 'JPEG', x, y, size.width, size.height)
}

const addImageEvidencePage = async (doc, settlement, group, payment, url, number, options = {}) => {
  doc.addPage()
  addEvidenceTitle(
    doc,
    `COBRO: ${group.chargeUid} — Imagen ${number}`,
    getEvidenceSubtitle(payment)
  )

  if (!isHttpUrl(url) || isUnavailableLocalUrl(url)) {
    drawEvidencePlaceholder(doc)
    return
  }

  try {
    const loader = options.loadImageDataUrl || loadImageDataUrl
    const imageData = await loader(url)
    drawEvidenceImage(doc, imageData)
  } catch (error) {
    console.error('Error al agregar imagen al PDF de liquidación:', error)
    drawEvidencePlaceholder(doc)
  }
}

const addFileEvidencePage = async (doc, settlement, group, payment, file, number, options = {}) => {
  const url = file?.url

  if (!isHttpUrl(url) || isUnavailableLocalUrl(url)) {
    doc.addPage()
    addEvidenceTitle(
      doc,
      `COBRO: ${group.chargeUid} — Adjunto ${number}`,
      getEvidenceSubtitle(payment)
    )
    drawEvidencePlaceholder(doc)
    return
  }

  if (isPdfUrl(url, file?.filename)) {
    try {
      const renderer = options.renderPdfToImageDataUrls || renderPdfToImageDataUrls
      const pages = await renderer(url)

      if (!pages.length) {
        throw new Error('El adjunto PDF no contiene páginas renderizables')
      }

      pages.forEach((page, pageIndex) => {
        doc.addPage()
        addEvidenceTitle(
          doc,
          `COBRO: ${group.chargeUid} — Adjunto ${number}${pages.length > 1 ? ` Página ${pageIndex + 1}` : ''}`,
          getEvidenceSubtitle(payment)
        )
        drawEvidenceImage(doc, page)
      })
      return
    } catch (error) {
      console.error('Error al renderizar adjunto PDF de liquidación:', error)
    }
  }

  if (isImageUrl(url, file?.filename)) {
    await addImageEvidencePage(doc, settlement, group, payment, url, number, options)
    return
  }

  doc.addPage()
  addEvidenceTitle(
    doc,
    `COBRO: ${group.chargeUid} — Adjunto ${number}`,
    getEvidenceSubtitle(payment)
  )
  drawEvidencePlaceholder(doc)
}

const addSettlementChargeEvidenceSection = async (
  doc,
  settlement,
  options = {},
  preferredStartY = null
) => {
  const groups = buildSettlementChargeGroups(settlement)

  if (groups.length === 0) return

  const titleStartY =
    typeof preferredStartY === 'number'
      ? ensureSectionStart(doc, preferredStartY, 60)
      : (() => {
          doc.addPage()
          return 90
        })()

  doc.setFont('helvetica', 'bold')
  doc.setFontSize(9)
  doc.text('COBROS Y COMPROBANTES ASOCIADOS A ESTA LIQUIDACIÓN:', PAGE_MARGIN_X, titleStartY)

  for (const group of groups) {
    addChargeDetailPage(doc, settlement, group)

    if (!group.hasEvidence) continue

    let imageNumber = 1
    let fileNumber = 1

    for (const payment of group.payments) {
      for (const image of safeArray(payment.images)) {
        await addImageEvidencePage(doc, settlement, group, payment, image.src, imageNumber, options)
        imageNumber += 1
      }

      for (const file of safeArray(payment.files)) {
        await addFileEvidencePage(doc, settlement, group, payment, file, fileNumber, options)
        fileNumber += 1
      }
    }
  }
}

const buildDepositRows = (settlement) =>
  buildReceiptRows(settlement).filter((row) => row.type === 'Depósito')

const buildDepositTableRows = (deposits = []) =>
  deposits.map((deposit) => [
    normalizePdfText(deposit?.uid),
    normalizePdfText(deposit?.bank),
    normalizePdfText(deposit?.number),
    formatPdfDate(deposit?.date),
    formatMoney(deposit?.amount || 0)
  ])

const getDepositEvidenceSubtitle = (deposit) => {
  const parts = [
    deposit?.bank,
    deposit?.number ? `No. ${deposit.number}` : null,
    deposit?.date ? formatPdfDate(deposit.date) : null,
    formatMoney(deposit?.amount || 0)
  ].filter(Boolean)

  return parts.length > 0 ? parts.join(' — ') : 'Comprobante de depósito'
}

const addDepositEvidencePage = async (doc, deposit, url, number, options = {}) => {
  doc.addPage()
  addEvidenceTitle(
    doc,
    `DEPÓSITO: ${deposit?.uid || deposit?.number || 'N/A'} — Imagen ${number}`,
    getDepositEvidenceSubtitle(deposit)
  )

  if (!isHttpUrl(url) || isUnavailableLocalUrl(url)) {
    drawEvidencePlaceholder(doc)
    return
  }

  try {
    const loader = options.loadImageDataUrl || loadImageDataUrl
    const imageData = await loader(url)
    drawEvidenceImage(doc, imageData)
  } catch (error) {
    console.error('Error al agregar imagen de depósito al PDF de liquidación:', error)
    drawEvidencePlaceholder(doc)
  }
}

const addSettlementDepositEvidenceSection = async (doc, settlement, options = {}) => {
  const deposits = buildDepositRows(settlement)

  if (deposits.length === 0) return

  doc.addPage()
  drawSectionTitle(doc, 'DEPÓSITOS ASOCIADOS A ESTA LIQUIDACIÓN:', PAGE_MARGIN_X, 70, 9)

  autoTable(doc, {
    head: [['# Depósito', 'Banco', 'Número de depósito', 'Fecha del depósito', 'Monto']],
    body: buildDepositTableRows(deposits),
    startY: 82,
    ...createCommonTableOptions({
      styles: { fontSize: 7.5, cellPadding: 3 },
      headStyles: { fontSize: 7.5, halign: 'left' }
    }),
    margin: { left: PAGE_MARGIN_X, right: PAGE_MARGIN_X },
    tableWidth: FULL_TABLE_WIDTH,
    columnStyles: {
      0: { cellWidth: 225 },
      1: { cellWidth: 185 },
      2: { cellWidth: 160 },
      3: { cellWidth: 110 },
      4: { cellWidth: 72, halign: 'right' }
    }
  })

  for (const deposit of deposits) {
    let imageNumber = 1

    for (const image of safeArray(deposit.images)) {
      await addDepositEvidencePage(doc, deposit, image.src, imageNumber, options)
      imageNumber += 1
    }
  }
}

export async function buildSettlementPdf(
  data,
  canSeeAnnulledDocumentsTab,
  origin = '',
  options = {}
) {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'letter' })

  // ===== CONFIG BASE =====
  doc.setFont('helvetica', 'normal')
  const pageRight = getPageRight(doc)

  // ===== EMPRESA =====
  const business = data?.business || {}
  const businessName = firstPresentValue(
    business.business_description,
    business.description,
    business.business_name,
    business.commercial_name,
    business.legal_name,
    business.name
  )
  const businessRuc = firstPresentValue(
    business.ruc,
    business.nit,
    business.tax_id,
    business.taxId,
    business.business_ruc
  )
  const businessAddress = firstPresentValue(
    business.address,
    business.business_address,
    business.location,
    business.direction
  )
  const businessPhone = firstPresentValue(
    business.phone_number,
    business.phone,
    business.telephone,
    business.mobile,
    business.business_phone
  )

  doc.setFontSize(9)
  doc.setFont('helvetica', 'normal')
  doc.text(businessName, PAGE_MARGIN_X, 38)

  doc.setFont('helvetica', 'normal')
  doc.text(`RUC: ${businessRuc}`, PAGE_MARGIN_X, 50)
  doc.text(`Dirección: ${businessAddress}`, PAGE_MARGIN_X, 62)
  doc.text(`Teléfono: ${businessPhone}`, PAGE_MARGIN_X, 74)
  drawInlineLabel(
    doc,
    'Usuario:',
    `${data?.user_uid || 'N/A'} - ${data?.user_full_name || 'N/A'}`,
    PAGE_MARGIN_X,
    86
  )

  // ===== ENCABEZADO DERECHA =====
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(11)
  doc.text('Liquidación', pageRight, 42, { align: 'right' })

  doc.setFontSize(9)
  drawRightInlineLabel(doc, 'N° de Liquidación:', data?.uid || 'N/A', pageRight, 58)
  drawRightInlineLabel(doc, 'N° ERP:', data?.erp_document_reference || '-', pageRight, 70)
  drawRightInlineLabel(
    doc,
    'Fecha de creación:',
    formatPdfDateTime(data?.collected_at),
    pageRight,
    83
  )

  let startY = FIRST_PAGE_TABLE_START_Y

  // ===== ESTILOS COMUNES =====
  const commonTableOptions = createCommonTableOptions()

  // ============================================================================
  // =======================   RESUMEN TOTAL   ==================================
  // ============================================================================
  drawSectionTitle(doc, 'RESUMEN TOTAL:', PAGE_MARGIN_X, startY - 10)

  const resumen = [
    ['Efectivo', formatMoney(data.cash_amount || 0)],
    ['Transferencia', formatMoney(data.transference_amount || 0)],
    ['Pago digital', formatMoney(data.payment_digital_amount || 0)],
    ['Depósito bancario', formatMoney(data.deposit_amount || 0)],
    ['Cheque', formatMoney(data.cheque_amount || 0)],
    ['Tarjeta de crédito', formatMoney(data.credit_card_amount || 0)],
    ['Vale digital', formatMoney(data.vale_digital_amount || 0)],
    ['Pago policía', formatMoney(data.payment_amount || 0)],
    ['TOTAL COBRADO', formatMoney(data.total_amount || 0)]
  ]

  if (!data.charge_uid || data.charge_uid.length === 0) {
    resumen.push(['TOTAL CRÉDITO', formatMoney(data.delivery_credit_amount || 0)])
  }

  autoTable(doc, {
    head: [['Tipo de pago', 'Monto']],
    body: resumen,
    startY,
    ...commonTableOptions,
    styles: { ...commonTableOptions.styles, halign: 'right' },
    headStyles: { ...commonTableOptions.headStyles },
    columnStyles: {
      0: { halign: 'left' },
      1: { halign: 'right' }
    },
    margin: { left: PAGE_MARGIN_X, right: PAGE_MARGIN_X },
    tableWidth: FIRST_PAGE_SUMMARY_WIDTH,
    didParseCell: ({ row, cell, section }) => {
      if (section === 'body' && row.index >= resumen.length - 2) {
        cell.styles.fontStyle = 'bold'
      }
    }
  })

  const resumenFinalY = doc.lastAutoTable.finalY

  // ============================================================================
  // =======================  DESGLOSE EFECTIVO  ================================
  // ============================================================================
  drawSectionTitle(doc, 'DESGLOSE DE EFECTIVO:', FIRST_PAGE_CASH_LEFT, startY - 10)

  const denominations = data.cash_count?.[0]?.denominations ?? data.cash_count?.denominations
  const cashBreakdown = denominations
    ? Object.entries(denominations).map(([den, qty]) => [den, qty])
    : [['N/A', 'N/A']]

  autoTable(doc, {
    head: [['Denominación', 'Cantidad']],
    body: cashBreakdown,
    startY,
    ...commonTableOptions,
    columnStyles: {
      0: { halign: 'left' },
      1: { halign: 'right' }
    },
    margin: { left: FIRST_PAGE_CASH_LEFT, right: PAGE_MARGIN_X },
    tableWidth: FIRST_PAGE_CASH_WIDTH
  })

  const desgloseFinalY = doc.lastAutoTable.finalY
  startY = Math.max(resumenFinalY, desgloseFinalY) + 40

  // ============================================================================
  // ======================= DOCUMENTOS ASOCIADOS ===============================
  // ============================================================================

  drawSectionTitle(doc, 'DOCUMENTOS ASOCIADOS A ESTA LIQUIDACIÓN:', PAGE_MARGIN_X, startY - 10)

  const {
    headers: headersReceived,
    rows: rowsReceived,
    rawData: rawReceivedData,
    columnStyles: receivedColumnStyles
  } = buildReceivedDocumentsPdfTable(data?.details, options.canWrite === true)

  autoTable(doc, {
    head: [headersReceived],
    body: rowsReceived,
    startY,
    ...commonTableOptions,
    columnStyles: receivedColumnStyles,
    margin: { left: PAGE_MARGIN_X, right: PAGE_MARGIN_X },
    tableWidth: FULL_TABLE_WIDTH
  })

  startY = doc.lastAutoTable.finalY + 40

  // ============================================================================
  // ================= RESUMEN RECIBIDOS ================================
  // ============================================================================

  startY = ensureSectionStart(doc, startY)
  drawSectionTitle(doc, 'RESUMEN POR DOCUMENTO TIPO DE DOCUMENTO:', PAGE_MARGIN_X, startY - 10)

  const resumenTipo = {}

  rawReceivedData.forEach((item) => {
    if (!resumenTipo[item.tipo]) resumenTipo[item.tipo] = 0
    resumenTipo[item.tipo] += Number(item.total)
  })

  const resumenTipoRows = Object.entries(resumenTipo).map(([tipo, total]) => [
    tipo,
    formatMoney(total)
  ])

  if (resumenTipoRows.length === 0) {
    resumenTipoRows.push(['N/A', 'N/A'])
  }

  autoTable(doc, {
    head: [['TIPO DE DOCUMENTO', 'TOTAL']],
    body: resumenTipoRows,
    startY,
    ...commonTableOptions,
    columnStyles: {
      0: { cellWidth: 610, halign: 'left' },
      1: { cellWidth: 142, halign: 'right' }
    },
    margin: { left: PAGE_MARGIN_X, right: PAGE_MARGIN_X },
    tableWidth: FULL_TABLE_WIDTH
  })

  startY = doc.lastAutoTable.finalY + 40

  // ============================================================================
  // ======================= DOCUMENTOS RECHAZADOS ===============================
  // ============================================================================

  drawSectionTitle(
    doc,
    'DOCUMENTOS RECHAZADOS ASOCIADOS A ESTA LIQUIDACIÓN:',
    PAGE_MARGIN_X,
    startY - 10
  )

  const headersRejected = [
    'FECHA',
    'HORA',
    'CÓDIGO',
    'CLIENTE',
    'NÚMERO DE DOCUMENTO',
    'NÚMERO DE RECHAZO',
    'TIPO DE DOCUMENTO',
    'MOTIVO RECHAZO',
    'TOTAL'
  ]

  let rowsRejected = []
  let rawRejectedData = []

  if (data.refunds?.length > 0) {
    data.refunds.forEach((refund) => {
      const motivo = (refund.refund_details?.[0]?.motive || refund.motive || 'N/A').trim()
      const rawTotal = Number(refund.total || 0)

      rawRejectedData.push({ motivo, total: rawTotal })

      rowsRejected.push([
        formatDate(refund.date_of_document),
        formatTime(refund.date_of_document),
        refund.client_db_ref || 'N/A',
        refund.client_description || 'N/A',
        refund.invoice_number || 'N/A',
        refund.db_ref || 'N/A',
        parseRefundType(refund.refund_type),
        motivo,
        formatMoney(rawTotal)
      ])
    })
  }

  if (rowsRejected.length === 0) {
    rowsRejected.push(Array(headersRejected.length).fill('N/A'))
  }

  autoTable(doc, {
    head: [headersRejected],
    body: rowsRejected,
    startY,
    ...commonTableOptions,
    columnStyles: {
      0: { halign: 'left' },
      1: { halign: 'left' },
      2: { halign: 'left' },
      3: { halign: 'left' },
      4: { halign: 'left' },
      5: { halign: 'left' },
      6: { halign: 'left' },
      7: { halign: 'left' },
      8: { halign: 'right' }
    }
  })

  startY = doc.lastAutoTable.finalY + 40

  // ============================================================================
  // ======================= RESUMEN MOTIVOS (RECHAZADOS) ========================
  // ============================================================================

  startY = ensureSectionStart(doc, startY)
  drawSectionTitle(doc, 'RESUMEN MOTIVOS DE RECHAZO POR DOCUMENTO:', PAGE_MARGIN_X, startY - 10)

  const resumenMotivosRechazo = {}

  rawRejectedData.forEach(({ motivo, total }) => {
    if (!resumenMotivosRechazo[motivo]) resumenMotivosRechazo[motivo] = 0
    resumenMotivosRechazo[motivo] += total
  })

  const resumenMotivosRows = Object.entries(resumenMotivosRechazo).map(([motivo, total]) => [
    motivo,
    formatMoney(total)
  ])

  if (resumenMotivosRows.length === 0) {
    resumenMotivosRows.push(['N/A', 'N/A'])
  }

  autoTable(doc, {
    head: [['MOTIVO DE RECHAZO', 'TOTAL']],
    body: resumenMotivosRows,
    startY,
    ...commonTableOptions,
    margin: { left: PAGE_MARGIN_X, right: PAGE_MARGIN_X },
    tableWidth: FULL_TABLE_WIDTH,
    columnStyles: {
      0: { cellWidth: 610, halign: 'left' },
      1: { cellWidth: 142, halign: 'right' }
    }
  })

  startY = doc.lastAutoTable.finalY + 40

  // ============================================================================
  // ======================= PRODUCTOS RECHAZADOS ===============================
  // ============================================================================

  let rowsProducts = []
  let rawRejectedProductsData = []

  if (data.refunds?.length > 0) {
    startY = ensureSectionStart(doc, startY, 100)
    drawSectionTitle(
      doc,
      'PRODUCTOS RECHAZADOS ASOCIADOS A ESTA LIQUIDACIÓN:',
      PAGE_MARGIN_X,
      startY - 10
    )

    const headersProducts = [
      'NÚMERO DE FACTURA',
      'CÓDIGO PRODUCTO',
      'CÓDIGO BARRA',
      'NOMBRE DEL PRODUCTO',
      'MOTIVO DE RECHAZO',
      'UNIDAD DE VENTA',
      'CANTIDAD',
      'PRECIO',
      'TOTAL'
    ]

    rowsProducts = []

    data.refunds.forEach((refund) => {
      const headerRow = [
        `${refund.invoice_number} | ${refund.client_db_ref} - ${refund.client_description}`,
        '',
        '',
        '',
        '',
        '',
        '',
        '__LINK__',
        null
      ]
      headerRow.isLinkRow = true
      headerRow.refundUuid = refund.uuid
      rowsProducts.push(headerRow)

      refund.refund_details.forEach((detail) => {
        const motivo = (detail.motive || refund.motive || 'N/A').trim()
        const rawTotal = Number(detail.extended_price || 0)

        rawRejectedProductsData.push({
          motivo,
          total: rawTotal
        })

        rowsProducts.push([
          '',
          detail.product_db_ref || 'N/A',
          detail.product_barcode || 'N/A',
          detail.product_description || 'N/A',
          motivo,
          detail.unit_of_sale || 'N/A',
          detail.quantity || 'N/A',
          formatMoney(detail.unit_price || 0.0),
          formatMoney(rawTotal)
        ])
      })
    })

    autoTable(doc, {
      head: [headersProducts],
      body: rowsProducts,
      startY,
      ...commonTableOptions,
      columnStyles: {
        6: { halign: 'right' },
        7: { halign: 'right' },
        8: { halign: 'right' }
      },

      didParseCell: (hookData) => {
        if (hookData.row.raw.isLinkRow) {
          const precioIdx = headersProducts.indexOf('PRECIO')
          const totalIdx = headersProducts.indexOf('TOTAL')
          if ([precioIdx, totalIdx].includes(hookData.column.index)) {
            hookData.cell.text = []
            hookData.cell.styles.fillColor = null
            hookData.cell.styles.lineWidth = 0
          }
        }
      },

      didDrawCell: (dataCell) => {
        const precioColIndex = headersProducts.indexOf('PRECIO')

        if (
          dataCell.section === 'body' &&
          dataCell.row.raw.isLinkRow &&
          dataCell.column.index === precioColIndex
        ) {
          const refundUuid = dataCell.row.raw.refundUuid
          if (refundUuid) {
            const url = `${origin}/devoluciones/${refundUuid}`
            const precioCell = dataCell.row.cells[precioColIndex]
            const totalCell = dataCell.row.cells[headersProducts.indexOf('TOTAL')]

            if (precioCell && totalCell) {
              const combinedWidth = precioCell.width + totalCell.width
              const linkText = 'ENLACE FACTURA'
              const textWidth = doc.getTextWidth(linkText)
              const offsetX = 9

              const x = precioCell.x + (combinedWidth - textWidth) / 2 + offsetX
              const y = dataCell.cell.y + dataCell.cell.height / 2 + 3

              doc.setFontSize(8)
              doc.setTextColor(0, 0, 255)
              doc.textWithLink(linkText, x, y, { url, target: '_blank' })
              doc.setTextColor(0, 0, 0)
              doc.setFontSize(8)
            }
          }
        }
      }
    })

    startY = doc.lastAutoTable.finalY + 40
  }

  // ============================================================================
  // ======================= RESUMEN MOTIVOS PRODUCTOS ===========================
  // ============================================================================

  if (rawRejectedProductsData.length > 0) {
    startY = ensureSectionStart(doc, startY)
    drawSectionTitle(doc, 'RESUMEN MOTIVOS DE RECHAZO POR PRODUCTO:', PAGE_MARGIN_X, startY - 10)

    const resumenMotivosProductos = {}

    rawRejectedProductsData.forEach(({ motivo, total }) => {
      if (!resumenMotivosProductos[motivo]) resumenMotivosProductos[motivo] = 0
      resumenMotivosProductos[motivo] += total
    })

    const resumenMotivosProductosRows = Object.entries(resumenMotivosProductos).map(
      ([motivo, total]) => [motivo, formatMoney(total)]
    )

    if (resumenMotivosProductosRows.length === 0) {
      resumenMotivosProductosRows.push(['N/A', 'N/A'])
    }

    autoTable(doc, {
      head: [['MOTIVO DE RECHAZO', 'TOTAL']],
      body: resumenMotivosProductosRows,
      startY,
      ...commonTableOptions,
      margin: { left: PAGE_MARGIN_X, right: PAGE_MARGIN_X },
      tableWidth: FULL_TABLE_WIDTH,
      columnStyles: {
        0: { cellWidth: 610, halign: 'left' },
        1: { cellWidth: 142, halign: 'right' }
      }
    })

    startY = doc.lastAutoTable.finalY + 40
  }

  // ============================================================================
  // =========================== DOCUMENTOS ANULADOS ============================
  // ============================================================================

  if (canSeeAnnulledDocumentsTab) {
    drawSectionTitle(
      doc,
      'DOCUMENTOS ANULADOS ASOCIADOS A ESTA LIQUIDACIÓN:',
      PAGE_MARGIN_X,
      startY - 10
    )

    const headersCancelled = [
      'FECHA',
      'HORA',
      'CÓDIGO',
      'CLIENTE',
      'DOCUMENTO',
      'TIPO DE DOCUMENTO',
      'ESTATUS',
      'TOTAL'
    ]

    const rowsCancelled = data.details
      .filter((d) => d.charge_status === 'cancelled')
      .map((d) => {
        const rawTotal = Number(d.child_document_total || 0)

        return [
          formatDate(d.date_of_charge),
          formatTime(d.date_of_charge),
          d.client_uid || 'N/A',
          d.client_name || 'N/A',
          d.child_document_reference || 'N/A',
          parseDocumentType(d.child_document_type) || 'N/A',
          'Anulado',
          formatMoney(rawTotal)
        ]
      })

    if (rowsCancelled.length === 0) {
      rowsCancelled.push(Array(headersCancelled.length).fill('N/A'))
    }

    autoTable(doc, {
      head: [headersCancelled],
      body: rowsCancelled,
      startY,
      ...commonTableOptions,
      columnStyles: {
        7: { halign: 'right' }
      }
    })

    startY = doc.lastAutoTable.finalY + 40
  }

  await addSettlementChargeEvidenceSection(doc, data, options, startY + 8)
  await addSettlementDepositEvidenceSection(doc, data, options)
  addPageFooters(doc, formatPdfDateTime(data?.collected_at, { twoDigitDate: true }))

  const filenameUser = sanitizeFilenamePart(data?.user_full_name)
  const filenameBusiness = sanitizeFilenamePart(
    firstPresentValue(
      data?.business_uid,
      data?.business?.business_uid,
      data?.business?.uid,
      data?.business?.name,
      businessName
    )
  )
  const filenameSettlement = sanitizeFilenamePart(data?.uid)

  doc.save(`Liquidacion_${filenameUser}_${filenameBusiness}_${filenameSettlement}.pdf`)
}

const deliveryAssignmentStatus = (status) => {
  if (status === 'to_be_assigned') return 'Por asignar'
  if (status === 'open') return 'Asignado'
  if (status === 'processing') return 'En proceso'
  return normalizePdfText(status, '-')
}

const deliveryAssignmentClientType = (isCash) => {
  if (isCash === true) return 'Contado'
  if (isCash === false) return 'Crédito'
  return '-'
}

/**
 * Builds the printable guide from the exact delivery-request rows currently
 * rendered by the assignment screen.  It deliberately has no KPI section:
 * those counts describe the whole operation rather than the filtered rows.
 */
export function buildDeliveryAssignmentGuidePdf({ documents = [], filters = [] } = {}) {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'letter' })
  const createdAt = formatPdfDateTime(new Date(), { twoDigitDate: true })
  const appliedFilters = filters.length > 0 ? filters.join(' | ') : 'Ninguno'

  doc.setFont('helvetica', 'bold')
  doc.setFontSize(16)
  doc.text('REPORTE: Asignación de entregas', PAGE_MARGIN_X, 38)

  const filtersLabel = 'Filtros aplicados:'
  const filtersY = 55
  doc.setFontSize(9)
  // Measure the label while its bold font is active. Measuring it after
  // switching to normal made the first filter overlap the colon/spaces.
  const filtersLabelWidth = doc.getTextWidth(filtersLabel)
  doc.text(filtersLabel, PAGE_MARGIN_X, filtersY)
  doc.setFont('helvetica', 'normal')
  const filterTextX = PAGE_MARGIN_X + filtersLabelWidth + 10
  const filterTextWidth = getPageWidth(doc) - filterTextX - PAGE_MARGIN_X
  const filterLines = doc.splitTextToSize(appliedFilters, filterTextWidth)
  doc.text(filterLines, filterTextX, filtersY)

  const headers = [
    'FECHA DE\nCREACIÓN',
    'CÓDIGO DE\nCLIENTE',
    'NOMBRE DEL CLIENTE',
    'TIPO DE\nCLIENTE',
    'DIRECCIÓN DEL CLIENTE',
    '# DE DOCUMENTO',
    'TIPO DE\nDOCUMENTO',
    'TOTAL',
    'ENTREGADOR\nASIGNADO',
    'FECHA DE\nENTREGA',
    'ESTADO'
  ]
  const rows = documents.map((document) => [
    document.document_created_date || document.created_date
      ? `${formatDate(document.document_created_date || document.created_date)}\n${formatTime(document.document_created_date || document.created_date)}`
      : '-',
    normalizePdfText(document.client_db_ref, '-'),
    normalizePdfText(document.client_name, '-'),
    deliveryAssignmentClientType(document.client_is_cash),
    normalizePdfText(document.address_delivery, '-'),
    normalizePdfText(document.delivery_reference, '-'),
    normalizePdfText(document.delivery_type_text || document.delivery_type, '-'),
    formatMoney(Number(document.total || 0)),
    document.salesman_db_ref
      ? `${document.salesman_db_ref} - ${normalizePdfText(document.salesman_name, '-')}`
      : '-',
    document.visit_date ? formatPdfDate(document.visit_date) : '-',
    deliveryAssignmentStatus(document.status)
  ])

  autoTable(doc, {
    head: [headers],
    body: rows,
    startY: filtersY + (filterLines.length - 1) * 10 + 18,
    theme: 'grid',
    styles: {
      font: 'helvetica',
      fontSize: 6.7,
      cellPadding: 4,
      valign: 'middle',
      overflow: 'linebreak',
      lineColor: SOFT_LINE_COLOR,
      lineWidth: 0.35
    },
    headStyles: {
      fillColor: [162, 158, 157],
      textColor: [255, 255, 255],
      font: 'helvetica',
      fontStyle: 'bold',
      fontSize: 6.4,
      cellPadding: 4,
      valign: 'middle'
    },
    margin: { left: PAGE_MARGIN_X, right: PAGE_MARGIN_X, bottom: 42 },
    columnStyles: {
      0: { cellWidth: 54 },
      1: { cellWidth: 54 },
      2: { cellWidth: 81 },
      3: { cellWidth: 48 },
      4: { cellWidth: 125 },
      5: { cellWidth: 82 },
      6: { cellWidth: 58 },
      7: { cellWidth: 50, halign: 'right' },
      8: { cellWidth: 70 },
      9: { cellWidth: 54 },
      10: { cellWidth: 48 }
    }
  })

  const totalPages = doc.getNumberOfPages()
  for (let page = 1; page <= totalPages; page += 1) {
    doc.setPage(page)
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(7)
    doc.text(`Fecha de creación: ${createdAt}`, PAGE_MARGIN_X, getPageHeight(doc) - 20)
    doc.text(`Página ${page} de ${totalPages}`, getPageRight(doc), getPageHeight(doc) - 20, {
      align: 'right'
    })
  }

  doc.save(`Guia_asignacion_entregas_${new Date().toISOString().slice(0, 10)}.pdf`)
}
